import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { ContentBlock } from '../types/contentBlock';
import {
  extractPreviewSource,
  assembleHtmlDocument,
  buildCodeMapFromFiles,
  mergeCodeMaps,
  type CodeMap,
  type PreviewSource,
  type PreviewableFile,
} from '../utils/codePreviewExtractor';

// ============================================
// 类型
// ============================================

export interface CodePreviewState {
  /** 预览面板是否打开 */
  readonly previewOpen: boolean;
  /** iframe 加载的 Blob URL */
  readonly previewUrl: string | null;
  /** 是否正在加载文件内容 */
  readonly loading: boolean;
  /** 打开预览（指定消息内容） */
  readonly previewMessage: (content: string, contentBlocks?: readonly ContentBlock[]) => void;
  /** 关闭预览 */
  readonly closePreview: () => void;
  /** 刷新当前预览 */
  readonly refreshPreview: () => void;
  /**
   * ✨ 新增：自动预览消息列表中最后一条含 HTML 的 assistant 消息
   * 用于未读通知跳转场景
   */
  readonly previewLastHtmlMessage: (
    messages: readonly ChatMessageLike[]
  ) => void;
}

interface ChatMessageLike {
  readonly role: 'user' | 'assistant';
  readonly status?: 'thinking' | 'done' | 'error';
  readonly content: string;
  readonly contentBlocks?: readonly ContentBlock[];
}

// ============================================
// 内部工具
// ============================================

/** 生成 Blob URL 并清理旧的 */
const createBlobUrl = (html: string, oldUrl: string | null): string => {
  if (oldUrl) URL.revokeObjectURL(oldUrl);
  const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
  return URL.createObjectURL(blob);
};

/**
 * 从消息列表中倒序查找最后一条含可预览 HTML 内容的 assistant done 消息
 */
const findLastPreviewableAssistant = (
  messages: readonly ChatMessageLike[]
): ChatMessageLike | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== 'assistant' || m.status !== 'done') continue;
    const source = extractPreviewSource(m.content, m.contentBlocks);
    if (source.hasPreview) return m;
  }
  return null;
};

// ============================================
// Hook
// ============================================

/**
 * 代码预览 Hook（v2）
 *
 * 改动点：
 * 1. 不再自动打开预览 — 只有用户点击"预览"按钮才会打开
 * 2. previewMessage(content, blocks) — 支持预览任意消息
 * 3. 监听 messages 长度变化（用户发新消息）→ 自动关闭预览
 * 4. ✨ 新增 previewLastHtmlMessage — 给未读通知场景用
 */
export const useCodePreview = (
  messages: readonly ChatMessageLike[]
): CodePreviewState => {
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const blobUrlRef = useRef<string | null>(null);
  const mountedRef = useRef<boolean>(true);

  // 记录当前正在预览的源数据，供 refresh 使用
  const currentSourceRef = useRef<PreviewSource | null>(null);

  // ---- 需求 4：用户发新消息时自动关闭预览 ----
  const prevMessageCountRef = useRef<number>(messages.length);

  useEffect(() => {
    const prevCount = prevMessageCountRef.current;
    const currCount = messages.length;
    prevMessageCountRef.current = currCount;

    // 消息数增加（说明有新消息）且预览正在打开 → 关闭
    if (currCount > prevCount && previewOpen) {
      setPreviewOpen(false);
    }
  }, [messages.length, previewOpen]);

  // ---- 组件卸载清理 ----
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // ---- 核心：构建预览 URL ----
  const buildAndSetPreview = useCallback(async (source: PreviewSource): Promise<void> => {
    if (!source.hasPreview) return;

    try {
      setLoading(true);

      let finalCodeMap: CodeMap;

      if (source.files.length > 0) {
        const fileCodeMap = await buildCodeMapFromFiles(source.files);
        if (!mountedRef.current) return;
        finalCodeMap = mergeCodeMaps(source.codeMap, fileCodeMap);
      } else {
        if (!source.codeMap) return;
        finalCodeMap = source.codeMap;
      }

      if (!finalCodeMap.html) return;

      const htmlDoc = assembleHtmlDocument(finalCodeMap);
      const url = createBlobUrl(htmlDoc, blobUrlRef.current);
      blobUrlRef.current = url;

      if (mountedRef.current) {
        setPreviewUrl(url);
        setPreviewOpen(true);
        currentSourceRef.current = source;
      }
    } catch (err) {
      console.error('[useCodePreview] 构建预览失败:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // ---- 需求 2：预览指定消息 ----
  const previewMessage = useCallback((
    content: string,
    contentBlocks?: readonly ContentBlock[]
  ): void => {
    const source = extractPreviewSource(content, contentBlocks);
    if (!source.hasPreview) return;
    void buildAndSetPreview(source);
  }, [buildAndSetPreview]);

  // ---- ✨ 新增：自动预览最后一条含 HTML 的 assistant 消息 ----
  const previewLastHtmlMessage = useCallback((
    msgs: readonly ChatMessageLike[]
  ): void => {
    const target = findLastPreviewableAssistant(msgs);
    if (!target) return;

    const source = extractPreviewSource(target.content, target.contentBlocks);
    if (!source.hasPreview) return;
    void buildAndSetPreview(source);
  }, [buildAndSetPreview]);

  // ---- 关闭预览 ----
  const closePreview = useCallback((): void => {
    setPreviewOpen(false);
  }, []);

  // ---- 刷新当前预览 ----
  const refreshPreview = useCallback((): void => {
    const source = currentSourceRef.current;
    if (!source?.hasPreview) return;
    void buildAndSetPreview(source);
  }, [buildAndSetPreview]);

  return {
    previewOpen,
    previewUrl,
    loading,
    previewMessage,
    closePreview,
    refreshPreview,
    previewLastHtmlMessage,
  };
};